import { SubTaskStatus } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import { capitalize } from 'lodash';
import { ArrowRightCircle, CheckCircle2, XCircle } from 'lucide-react';
import React from 'react';
import { useTranslation } from '../../i18next';
import { useTheme } from '../../themes';
import { ButtonWithPromise } from '../ButtonWithPromise';

const ElementWithCompletionLabel = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing(1)}px;
  align-items: center;
  min-height: ${(p) => p.theme.spacing(3)}px;
`;

const StatusLabelContainer = styled('div')<{
  status: 'COMPLETE' | 'SKIPPED' | 'ERROR';
}>((p) => ({
  '&&': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
    color:
      p.status === 'COMPLETE'
        ? p.theme.custom.colors.success.main
        : p.status === 'ERROR'
        ? p.theme.custom.colors.error.main
        : p.theme.custom.colors.gray.w400,
    gap: p.theme.spacing(0.5)
  }
}));

const STATUS_ICON_SIZE = 16;

export const CompletedLabel = () => {
  const { t } = useTranslation();
  return (
    <StatusLabelContainer status="COMPLETE">
      <CheckCircle2 size={STATUS_ICON_SIZE} />
      <span>{t('subTask.status.complete')}</span>
    </StatusLabelContainer>
  );
};

export const SkippedLabel = () => {
  const { t } = useTranslation();
  return (
    <StatusLabelContainer status="SKIPPED">
      <ArrowRightCircle size={STATUS_ICON_SIZE} />
      <span>{t('subTask.status.skipped')}</span>
    </StatusLabelContainer>
  );
};

export const ErroredLabel = () => {
  const { t } = useTranslation();
  return (
    <StatusLabelContainer status="ERROR">
      <XCircle size={STATUS_ICON_SIZE} />
      <span>{t('subTask.status.error')}</span>
    </StatusLabelContainer>
  );
};

export const CompleteButton: React.FC<{
  onClick?: () => Promise<any>;
  startIcon?: JSX.Element;
  disabled?: boolean;
  status: SubTaskStatus;
  mode: 'LIVE' | 'PREVIEW';
}> = ({ onClick, children, startIcon, disabled, status, mode }) => {
  const { t } = useTranslation();
  const commonProps = {
    variant: 'contained',
    color: 'primary',
    startIcon,
    disabled: disabled || status !== SubTaskStatus.OPEN
  } as const;

  if (!onClick) {
    return <Button {...commonProps}>{children}</Button>;
  }
  return (
    <ElementWithCompletionLabel>
      <ButtonWithPromise
        onClick={mode === 'LIVE' ? onClick : () => Promise.resolve()}
        pending={capitalize(t('completing'))}
        {...commonProps}
      >
        {children}
      </ButtonWithPromise>
      {status === SubTaskStatus.DONE && <CompletedLabel />}
      {status === SubTaskStatus.SKIPPED && <SkippedLabel />}
    </ElementWithCompletionLabel>
  );
};

export const CompleteLabelButton: React.FC<{
  startIcon?: JSX.Element;
  disabled?: boolean;
  status: SubTaskStatus;
  keepEnabledWhenNotOpen?: boolean;
  htmlFor?: string;
}> = ({
  children,
  startIcon,
  disabled,
  status,
  keepEnabledWhenNotOpen,
  htmlFor
}) => {
  return (
    <ElementWithCompletionLabel>
      <Button
        variant={'contained'}
        color={'primary'}
        startIcon={startIcon}
        disabled={
          disabled || (!keepEnabledWhenNotOpen && status !== SubTaskStatus.OPEN)
        }
        component="label"
        htmlFor={htmlFor}
      >
        {children}
      </Button>
      {status === SubTaskStatus.DONE && <CompletedLabel />}
      {status === SubTaskStatus.SKIPPED && <SkippedLabel />}
    </ElementWithCompletionLabel>
  );
};

export const CompleteLink: React.FC<{
  href: string;
  startIcon?: JSX.Element;
  disabled?: boolean;
  status: SubTaskStatus;
  mode: 'LIVE' | 'PREVIEW';
  onClick?: () => void; // optional - mainly used to deal with tracking
  customStyles?: React.CSSProperties;
}> = ({ href, children, startIcon, disabled, status, mode, onClick, customStyles }) => {
  const theme = useTheme();
  const additionalStyles: React.CSSProperties = {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'left',
    ...customStyles
  };

  return (
    <ElementWithCompletionLabel>
      {mode === 'LIVE' ? (
        <Button
          href={href}
          variant="text"
          color="primary"
          startIcon={startIcon}
          disabled={disabled}
          target="_blank"
          rel="noopener"
          onClick={onClick}
          style={{
            fontWeight: theme.typography.fontWeightBold,
            ...additionalStyles
          }}
        >
          {children}
        </Button>
      ) : (
        <Button
          variant="text"
          color="primary"
          startIcon={startIcon}
          disabled={disabled}
          style={{ fontWeight: 400, ...additionalStyles }}
        >
          {children}
        </Button>
      )}
      {status === SubTaskStatus.DONE && <CompletedLabel />}
      {status === SubTaskStatus.SKIPPED && <SkippedLabel />}
    </ElementWithCompletionLabel>
  );
};
